/**
 * Module access helpers (ORCH-334)
 *
 * Purpose: Pure checks behind `useAuth().can` / `hasModule` / `hasFeature`, kept
 *          outside the context so routes, nav and tests share one implementation.
 * Logic: A module is held when the user's `modules` map has it at the needed
 *        level and also holds its parent (ORCH-21 §3.12 rule 1). The parent is the
 *        longest other module key that prefixes its value (ADMIN_MDM_MANAGER → ADMIN).
 */

import { ModuleKey, PermissionKey } from '../constants/permissionKeys';
import { ModuleLevel, User } from '../types/auth';

const MODULE_KEYS = Object.values(ModuleKey) as ModuleKey[];

/** The module's parent, or undefined for a top-level module. */
export const parentModule = (key: ModuleKey): ModuleKey | undefined =>
  MODULE_KEYS.filter((candidate) => key.startsWith(`${candidate}.`)).sort(
    (a, b) => b.length - a.length
  )[0];

/** Whether the user holds `key` in the active tenant. */
export const userCan = (user: User | null | undefined, key: PermissionKey): boolean =>
  !!user?.permissions?.includes(key);

/**
 * The features enabled for the active tenant (ORCH-21 §3.12 rule 2)
 *
 * Purpose: What a tenant is licensed for, for callers that grey out an area the
 *          tenant doesn't have.
 * Logic: The active tenant's own `availableTenants` entry is read first: the
 *        user's flat `feature_toggles` carries the active tenant's only on
 *        login, select-tenant and refresh, and comes back empty from
 *        `/auth/me`, which is what a page reload rehydrates from.
 * Input: user - the signed-in user, or null
 * Output: the enabled feature keys, empty when none are known
 */
export const activeTenantFeatures = (user: User | null | undefined): string[] => {
  const active = user?.activeTenant?.tenant_id;
  const entry = user?.availableTenants?.find((t) => t.tenant_id === active);
  return entry?.feature_toggles ?? user?.feature_toggles ?? [];
};

/** Whether the user holds `key` at `level` (Editable satisfies Read-only), with its parents. */
export const userHasModule = (
  user: User | null | undefined,
  key: ModuleKey,
  level: ModuleLevel = 'read'
): boolean => {
  const held = user?.modules?.[key];
  if (!held || (level === 'edit' && held !== 'edit')) {
    return false;
  }
  const parent = parentModule(key);
  return parent === undefined || userHasModule(user, parent);
};
